import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { CourseOfferingExecutionsApiService } from 'app/library/course-offering-executions/services/course-offering-executions.api-service';
import { AddModules } from 'app/library/module/actions/add-entities.action';
import { Module } from 'app/library/module/models/module.model';
import { newModule } from 'app/library/module/models/newModule';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import { ModuleState } from '../../module/models/module.state';
import {
    DeselectSelectedModules,
    FetchAllExecutions,
    FetchAllExecutionsFailure,
    FetchAllExecutionsSuccess,
    FetchAllModules,
    FetchAllModulesFailure,
    FetchAllModulesSuccess,
    FetchIncludedModules,
    SearchAllModules,
    SelectFilteredModules,
    SetAllModules,
    SortAllExecutions,
    SortAllModules,
    ToggleSelectedModule,
    UnsetAllModules,
} from '../actions';
import { AllModulesApiService } from '../services/all-modules.api-service';
import { AllModulesStateModel } from './all-modules.state-model';

@State<AllModulesStateModel>({
    name: 'allModules',
    defaults: new AllModulesStateModel(),
})
@Injectable()
export class AllModulesState extends SubentityState<Module> {
    @Selector([ModuleState.getEntities])
    static getEntities(state: AllModulesStateModel, modules: Module[]): Module[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.list.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Module], b: [number, Module]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Module]) => x[1]);
    }

    @Selector([ModuleState.getEntities])
    static getFilteredEntities(state: AllModulesStateModel, modules: Module[]): Module[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Module], b: [number, Module]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Module]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: AllModulesStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getFiltersList(state: AllModulesStateModel): string[] {
        return state.filters.list;
    }

    @Selector()
    static getSearchTerm(state: AllModulesStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: AllModulesStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: AllModulesStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: AllModulesStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getExecutionsSortBy(state: AllModulesStateModel): string {
        return state.allExecutionsSortBy;
    }

    @Selector()
    static getExecutionsSortOrder(state: AllModulesStateModel): SortDirection {
        return state.allExecutionsSortOrder;
    }

    @Selector()
    static getPageSize(state: AllModulesStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: AllModulesStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: AllModulesStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    constructor(
        private _allModulesService: AllModulesApiService,
        private _courseOfferingExecutionsService: CourseOfferingExecutionsApiService,
        private store: Store,
        private action$: Actions
    ) {
        super(null, 0);

        this._propstoSearch = ['module', ...Module.props_to_search];
    }

    @Action(SetAllModules)
    setAll(ctx: StateContext<AllModulesStateModel>, action: SetAllModules): void {
        this._setSubentities(ctx, action, AddModules);
    }

    @Action(UnsetAllModules)
    unsetAll(ctx: StateContext<AllModulesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchAllModules)
    fetchAll(ctx: StateContext<AllModulesStateModel>, action: FetchAllModules): void {
        if (action.payload.patchState) {
            ctx.patchState({
                pageSize: action.payload.pageSize,
                pageNumber: action.payload.pageNumber,
            });
        }

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetAllModules,
            FetchAllModulesSuccess,
            FetchAllModulesFailure,
            this._allModulesService
                .findModules(
                    action.payload.filterValues,
                    action.payload.search,
                    action.payload.sortBy,
                    action.payload.sortOrder,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.kind,
                    action.payload.ids,
                    action.payload.context
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => newModule(query));
                    })
                )
        );
    }

    @Action(FetchAllExecutions)
    fetchAllExecutions(ctx: StateContext<AllModulesStateModel>, action: FetchAllExecutions): void {
        if (action.payload) {
            ctx.patchState({
                pageSize: action.payload.pageSize,
                pageNumber: action.payload.pageNumber,
            });
        }

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetAllModules,
            FetchAllExecutionsSuccess,
            FetchAllExecutionsFailure,
            this._courseOfferingExecutionsService
                .getAllExecutions(
                    action.payload?.search,
                    action.payload?.pageSize,
                    action.payload?.pageNumber,
                    action.payload?.sortBy,
                    action.payload?.sortOrder,
                    action.payload?.statusesIds,
                    action.payload?.tags,
                    action.payload?.types,
                    action.payload?.extendedAttributesFilters,
                    action.payload?.executionDate,
                    action.payload?.columns
                )
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => newModule(query));
                    })
                )
        );
    }

    @Action(FetchAllExecutionsSuccess)
    fetchAllExecutionsSuccess(ctx: StateContext<AllModulesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchAllExecutionsFailure)
    fetchAllExecutionsFailure(ctx: StateContext<AllModulesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchIncludedModules)
    fetchIncludedModules(ctx: StateContext<AllModulesStateModel>, action: FetchIncludedModules): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetAllModules,
            FetchAllModulesSuccess,
            FetchAllModulesFailure,
            this._allModulesService
                .fetchIncludedModules(action.payload.reportId, action.payload.startDate, action.payload.endDate, action.payload.pageSize, action.payload.pageNumber)
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => newModule(query));
                    })
                )
        );
    }

    @Action(FetchAllModulesSuccess)
    fetchAllSuccess(ctx: StateContext<AllModulesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchAllModulesFailure)
    fetchAllFailure(ctx: StateContext<AllModulesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedModule)
    toggleSelect(ctx: StateContext<AllModulesStateModel>, action: ToggleSelectedModule): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModules)
    selectAll(ctx: StateContext<AllModulesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModules)
    deselectAll(ctx: StateContext<AllModulesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchAllModules)
    search({ patchState }: StateContext<AllModulesStateModel>, { payload }: SearchAllModules): void {
        sessionStorage.setItem(payload.sessionStorageKey, payload.searchItem);

        patchState({
            searchTerm: payload.searchItem,
        });
    }

    @Action(SortAllModules)
    sort({ patchState }: StateContext<AllModulesStateModel>, { payload }: SortAllModules): void {
        sessionStorage.setItem('allModulesSortBy', payload.active);
        sessionStorage.setItem('allModulesSortOrder', payload.direction);

        patchState({
            sortBy: payload.active,
            sortOrder: payload.direction,
        });
    }

    @Action(SortAllExecutions)
    sortAllExecutions({ patchState }: StateContext<AllModulesStateModel>, { payload }: SortAllModules): void {
        sessionStorage.setItem('allExecutionsSortBy', payload.active);
        sessionStorage.setItem('allExecutionsSortOrder', payload.direction);

        patchState({
            allExecutionsSortBy: payload.active,
            allExecutionsSortOrder: payload.direction,
        });
    }
}
